import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Breadcrumb} from '@app/models/breadcrumb.model';
import {IHelpVideo} from '@app/models/video-library/help-video.model';
import {VideoLibraryAPIService} from '@app/shared/api/video-library.api.service';
import {BreadcrumbService} from '@app/shared/breadcrumbs/breadcrumbs.service';
import {PillType} from '@app/shared/components/pill/pill.component';
import {Globals} from '@app/shared/globals/globals';
import {NotifyUtils} from '@app/shared/utils/notify.utils';

interface PageState {
  loading: boolean;
  error: boolean;
  errorMessage: string;
  markingVideoWatched: boolean;
}

@Component({
  selector: 'app-video-library-individual',
  templateUrl: './video-library-individual.component.html',
  styleUrls: ['./video-library-individual.component.scss']
})
export class VideoLibraryIndividualComponent implements OnInit {
  public readonly ePillType = PillType;

  video: IHelpVideo;
  breadcrumb: Breadcrumb;
  state: PageState;
  watchStateChangedManually: boolean;

  constructor(
    public globals: Globals,
    public route: ActivatedRoute,
    private videoLibraryAPIService: VideoLibraryAPIService,
    private breadcrumbService: BreadcrumbService,
    private notifyUtils: NotifyUtils
  ) {
    this.video = undefined!;
    this.breadcrumb = this.breadcrumbService.init(this.route);
    this.state = {
      loading: true,
      error: false,
      errorMessage: '',
      markingVideoWatched: false
    };
    this.watchStateChangedManually = false;
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(
      (params) => {
        const id = params.get('id');

        if (!id) {
          console.error('[Video Library] Invalid video ID');
        }

        this.getData(Number(id));
      }
    );
  }

  ngOnDestroy(): void {
    this.breadcrumbService.remove(this.breadcrumb);
  }

  getData(id: number): void {
    this.videoLibraryAPIService.getVideoById(id)
      .subscribe(
        (video) => {
          this.video = video;
          this.breadcrumbService.updateCurrentBreadcrumb(video.title, null);
          this.state.loading = false;
        }
      );
  }

  doError(message: string): void {
    this.state.loading = false;
    this.state.error = true;
    this.state.errorMessage = message;
  }

  markVideoWatched(triggeredManually = false): void {
    if (this.video.watched) { return; }
    if (this.state.markingVideoWatched) { return; }

    this.state.markingVideoWatched = true;

    this.videoLibraryAPIService.markVideoAsWatched(this.video.id)
      .subscribe(
        (res) => {
          this.video = res;
          this.state.markingVideoWatched = false;
          if (triggeredManually) {
            this.notifyUtils.notify('Video marked as watched');
          }
        },
        (err) => {
          this.timeoutWatchStateReset(triggeredManually);
        }
      );
  }

  markVideoNotWatched(triggeredManually = false): void {
    if (!this.video.watched) { return; }
    if (this.state.markingVideoWatched) { return; }

    this.state.markingVideoWatched = true;

    this.videoLibraryAPIService.markVideoAsNotWatched(this.video.id)
      .subscribe(
        (res) => {
          this.video = res;
          this.state.markingVideoWatched = false;
          if (triggeredManually) {
            this.notifyUtils.notify('Video marked as unwatched');
          }
        },
        (err) => {
          this.timeoutWatchStateReset(triggeredManually);
        }
      );
  }

  automaticallyMarkVideoWatched(): void {
    if (this.watchStateChangedManually) { return; }

    this.markVideoWatched(false);
  }

  manuallyMarkVideoWatched(): void {
    this.watchStateChangedManually = true;
    this.markVideoWatched(true);
  }

  manuallyMarkVideoNotWatched(): void {
    this.watchStateChangedManually = true;
    this.markVideoNotWatched(true);
  }

  timeoutWatchStateReset(timeoutEnabled: boolean): void {
    if (timeoutEnabled) {
      this.state.markingVideoWatched = false;
    } else {
      setTimeout(() => {
        this.state.markingVideoWatched = false;
      }, 5000);
    }
  }
}
